import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSubscription, useApolloClient, useReactiveVar } from "@apollo/client";
import { ORDER_UPDATE_SUBSCRIPTION } from "../pages/orders/queries";
import { GETORDER } from "../pages/pos/queries";
import { PrinterContext } from "../../context/printer-context";
import useSettings from "../../hooks/settings";
import { activeBranchVar } from "../../middleware/cache";

interface PendingOrder {
  id: string;
  total: number;
  type: string;
  createdAt: number; // timestamp ms
}

const CHIME_REPEAT_MS = 5_000; // Repeat chime every 5 seconds for app orders
const URGENT_THRESHOLD_MS = 60 * 1000; // 1 minute
const DISMISSED_STORAGE_KEY = "foo_dismissed_order_alerts";
const POS_ALERTS_PREF_KEY = "foo_pos_order_alerts_enabled";

/** Read the POS-order-alerts preference. Defaults to true (on). */
export function getPosAlertsEnabled(): boolean {
  const raw = localStorage.getItem(POS_ALERTS_PREF_KEY);
  if (raw === null) return true; // default on
  return raw === "true";
}

/** Toggle the POS-order-alerts preference. */
export function setPosAlertsEnabled(enabled: boolean) {
  localStorage.setItem(POS_ALERTS_PREF_KEY, String(enabled));
}

// --- Web Audio API sound generators (no external files) ---
let audioCtx: AudioContext | null = null;

const getAudioCtx = (): AudioContext => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioCtx;
};

const playChime = () => {
  try {
    const ctx = getAudioCtx();
    const now = ctx.currentTime;

    // Two-tone gentle chime
    [523.25, 659.25].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.6);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.6);
    });
  } catch (e) {
    console.warn("Audio chime failed:", e);
  }
};

const playUrgentAlarm = () => {
  try {
    const ctx = getAudioCtx();
    const now = ctx.currentTime;

    // Rapid alternating alarm tone
    for (let i = 0; i < 6; i++) {
      const freq = i % 2 === 0 ? 880 : 660;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.2, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.1);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.12);
    }
  } catch (e) {
    console.warn("Audio alarm failed:", e);
  }
};

/** Read dismissed order IDs from localStorage. Prunes entries older than 24h. */
function loadDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return new Set();
    const entries: { id: string; ts: number }[] = JSON.parse(raw);
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const valid = entries.filter((e) => e.ts > cutoff);
    return new Set(valid.map((e) => e.id));
  } catch {
    return new Set();
  }
}

/** Persist a dismissed order ID to localStorage with a timestamp. */
function saveDismissedId(id: string) {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const entries: { id: string; ts: number }[] = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const valid = entries.filter((e) => e.ts > cutoff && e.id !== id);
    valid.push({ id, ts: Date.now() });
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(valid));
  } catch {
    // ignore
  }
}

/** Parse the creation_date coming from the GraphQL API. It's either a numeric
 *  timestamp string (e.g. "1681234567890") or an ISO date string. */
function parseCreationDate(raw?: string): number {
  if (!raw) return Date.now();
  // Try numeric timestamp first (most common from this backend)
  const numeric = parseInt(raw, 10);
  if (!isNaN(numeric) && String(numeric).length >= 10) {
    // Millisecond timestamp (13 digits) vs second timestamp (10 digits)
    return numeric > 1e12 ? numeric : numeric * 1000;
  }
  // Fall back to ISO / date-string parse
  const d = new Date(raw).getTime();
  return isNaN(d) ? Date.now() : d;
}

/** Read orders from localStorage (written by the orders page). */
function readOrdersFromStorage(): any[] {
  try {
    return JSON.parse(localStorage.getItem("all_orders") || "[]");
  } catch {
    return [];
  }
}

const OnlineOrderAlert: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const printer = useContext(PrinterContext);
  const [settings] = useSettings();
  const activeBranch = useReactiveVar(activeBranchVar);
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(loadDismissedIds);
  const knownIds = useRef<Set<string>>(new Set());
  const printedOrderIds = useRef<Set<string>>(new Set());
  const urgentIntervalRef = useRef<NodeJS.Timer | null>(null);
  const chimeIntervalRef = useRef<NodeJS.Timer | null>(null);
  const [tick, forceUpdate] = useState(0);

  // Auto-print a new order's restaurant/client copies. Lives here (rather than
  // on the Orders page) because this component is mounted app-wide in App.tsx
  // and must keep printing regardless of which route the cashier is on —
  // Orders unmounts when navigating away from "/", which used to silently
  // stop auto-print for anyone not sitting on the home screen.
  const autoPrintOrder = async (orderId: string) => {
    if (!settings.print_upfront && !settings.restaurant_copy) return;
    if (printedOrderIds.current.has(orderId)) return;
    printedOrderIds.current.add(orderId);

    try {
      const { data } = await apolloClient.query({
        query: GETORDER,
        variables: { order_id: parseInt(orderId) },
        fetchPolicy: "network-only",
      });
      if (data?.getOrder?.products?.length) {
        if (settings.restaurant_copy) {
          await printer.print(data.getOrder, "restaurant");
        }
        if (settings.print_upfront) {
          await printer.print(data.getOrder, "client");
        }
      }
    } catch (err) {
      console.error("Auto-print error for order", orderId, err);
    }
  };

  // Single app-wide WebSocket subscription for order updates. When an event
  // arrives we dispatch a custom DOM event so any page (e.g. the orders list)
  // can react without running its own subscription, and auto-print here
  // directly since this component is always mounted.
  const { data: _subData } = useSubscription(ORDER_UPDATE_SUBSCRIPTION, {
    shouldResubscribe: true,
    onSubscriptionData: (opts) => {
      const order = opts?.subscriptionData?.data?.orderUpdate;
      window.dispatchEvent(new CustomEvent("foo:order-updated"));
      if (!order) return;
      const sameBranch = !activeBranch || String(order.branch?.id) === String(activeBranch);
      const isNew = order.status === "placed" || order.status === "new";
      if (sameBranch && isNew) {
        autoPrintOrder(order.id);
      }
    },
  });

  // Tick every 10s to update "waiting" timers and check urgency
  useEffect(() => {
    const ticker = setInterval(() => forceUpdate((n) => n + 1), 10_000);
    return () => clearInterval(ticker);
  }, []);

  // Auto-dismiss when the user navigates to an order page (e.g. from the list,
  // a table tap, a link — not just from the notification "Ver pedido" button).
  useEffect(() => {
    const match = location.pathname.match(/^\/order\/(\d+)/);
    if (match) {
      const orderId = match[1];
      if (!dismissedIds.has(orderId)) {
        saveDismissedId(orderId);
        setDismissedIds((prev) => new Set(prev).add(orderId));
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-evaluate pending orders whenever subscription fires, tick updates, or
  // dismissals change. Reads from localStorage where the orders page writes.
  // POS order alerts can be disabled in preferences; online orders always show.
  useEffect(() => {
    const allOrders = readOrdersFromStorage();
    const posAlertsOn = getPosAlertsEnabled();
    const newOrders = allOrders.filter(
      (o: any) =>
        (o.type === "online" && o.status === "placed") ||
        (posAlertsOn && o.type === "pos" && o.status === "new")
    );

    const newPending: PendingOrder[] = [];
    let hasNew = false;

    newOrders.forEach((o: any) => {
      if (!knownIds.current.has(o.id) && !dismissedIds.has(o.id)) {
        hasNew = true;
      }
      knownIds.current.add(o.id);

      if (!dismissedIds.has(o.id)) {
        const existing = pendingOrders.find((p) => p.id === o.id);
        newPending.push({
          id: o.id,
          total: o.total,
          type: o.type,
          createdAt: existing?.createdAt || parseCreationDate(o.creation_date),
        });
      }
    });

    setPendingOrders(newPending);

    if (hasNew) {
      playChime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_subData, tick, dismissedIds]);

  // Repeating chime: play chime every 5s while there are pending app orders
  const hasOnlinePending = pendingOrders.some((o) => o.type === "online");

  const startChimeRepeat = useCallback(() => {
    if (chimeIntervalRef.current) return;
    chimeIntervalRef.current = setInterval(playChime, CHIME_REPEAT_MS);
  }, []);

  const stopChimeRepeat = useCallback(() => {
    if (chimeIntervalRef.current) {
      clearInterval(chimeIntervalRef.current);
      chimeIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (hasOnlinePending) {
      startChimeRepeat();
    } else {
      stopChimeRepeat();
    }
    return stopChimeRepeat;
  }, [hasOnlinePending, startChimeRepeat, stopChimeRepeat]);

  // Urgent alarm: if any order has been waiting > 1 min, play alarm every 8s
  const hasUrgent = pendingOrders.some(
    (o) => Date.now() - o.createdAt >= URGENT_THRESHOLD_MS
  );

  const startUrgentAlarm = useCallback(() => {
    if (urgentIntervalRef.current) return;
    stopChimeRepeat(); // Stop chime when escalating to urgent alarm
    playUrgentAlarm();
    urgentIntervalRef.current = setInterval(playUrgentAlarm, 8_000);
  }, [stopChimeRepeat]);

  const stopUrgentAlarm = useCallback(() => {
    if (urgentIntervalRef.current) {
      clearInterval(urgentIntervalRef.current);
      urgentIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (hasUrgent) {
      startUrgentAlarm();
    } else {
      stopUrgentAlarm();
    }
    return stopUrgentAlarm;
  }, [hasUrgent, startUrgentAlarm, stopUrgentAlarm]);

  const dismissOrder = (id: string) => {
    saveDismissedId(id);
    setDismissedIds((prev) => new Set(prev).add(id));
  };

  const goToOrder = (id: string) => {
    dismissOrder(id);
    history.push(`/order/${id}`);
  };

  if (pendingOrders.length === 0) return null;

  return (
    <div className="online-order-alert-container">
      {pendingOrders.map((order) => {
        const waitMs = Date.now() - order.createdAt;
        const isUrgent = waitMs >= URGENT_THRESHOLD_MS;
        const waitSec = Math.max(0, Math.floor(waitMs / 1000));
        const waitMin = Math.floor(waitSec / 60);
        const waitDisplay =
          waitMin > 0 ? `${waitMin}m ${waitSec % 60}s` : `${waitSec}s`;

        return (
          <div
            key={order.id}
            className={`online-order-alert ${isUrgent ? "urgent" : ""}`}
          >
            <div className="alert-icon">
              <i className={`fas ${order.type === "pos" ? "fa-cash-register" : "fa-shopping-bag"}`} />
            </div>
            <div className="alert-content">
              <div className="alert-title">
                {order.type === "pos"
                  ? `Nueva orden #${order.id}`
                  : `Pedido en línea #${order.id}`}
              </div>
              <div className="alert-meta">
                <span className="alert-total">
                  ${Number(order.total).toFixed(2)}
                </span>
                <span className="alert-wait">
                  <i className="fas fa-clock" /> {waitDisplay}
                </span>
              </div>
            </div>
            <div className="alert-actions">
              <button
                className="btn btn-sm btn-warning alert-view-btn"
                onClick={() => goToOrder(order.id)}
              >
                Ver pedido
              </button>
              <button
                className="btn btn-sm btn-outline-secondary alert-dismiss-btn"
                onClick={() => dismissOrder(order.id)}
                title="Descartar"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OnlineOrderAlert;
